import React, { Suspense, ComponentType, ReactNode, useState, useEffect, useTransition, createContext, useContext, useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { parseErrors } from '../utils/graphQLErrors';
import PageTitleAndNav from './common/PageTitleAndNav';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ButtonProps } from './common/Button';

interface ScreenProps {
  children: ReactNode;
}

function ErrorFallback({ error }: { error: any }) {
  const [parsedErrors] = useState(parseErrors(error));

  if (!parsedErrors.length) {
    return null;
  }

  console.log(parsedErrors);

  return (
    <div className="p-8 rounded-lg bg-red-50 border border-red-100">
      {parsedErrors.map((parsedError) => (
        <div key={parsedError.message}>
          <h2 className="text-lg font-semibold text-red-800 mb-2">{parsedError.status}</h2>
          <p className="text-red-600">{parsedError.message}</p>
          <pre className="text-red-600">{JSON.stringify(parsedError.debugInfo, null, 2)}</pre>
          {parsedError.stack && (
            <pre className="text-red-600">{parsedError.stack.map((line: string) => <span key={line}>{line}<br /></span>)}</pre>
          )}
        </div>
      ))}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex justify-center items-center p-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );
}

export type NavStep = {
  label: string | ReactNode;
  icon: React.ReactNode;
  subSteps?: Record<string, Omit<NavStep, 'subSteps'>>;
};

type ScreenContextType = {
  nav: {
    title: string | ReactNode | null;
    titleText: string | null;
    titleRightSide: ReactNode | null;
    currentNavStep: string | null;
    currentSubStep: string | null;
    navSteps: Record<string, NavStep> | null;
    defaultNavStep: string | null;
    defaultSubStep: string | null;
    actions: ButtonProps[] | { left?: ButtonProps[], right?: ButtonProps[] };
    setTitle: (title: string | ReactNode) => void;
    setTitleRightSide: (titleRightSide: ReactNode) => void;
    setTitleText: (titleText: string) => void;
    setNavSteps: ({ navSteps, defaultNavStep, defaultSubStep }: { navSteps: Record<string, NavStep>, defaultNavStep: string, defaultSubStep: string | null }) => void;
    setActions: (actions: ButtonProps[] | { left?: ButtonProps[], right?: ButtonProps[] }) => void;
  }
};

const ScreenContext = createContext<ScreenContextType>({
  nav: {
    title: null,
    titleText: null,
    titleRightSide: null,
    currentNavStep: null,
    currentSubStep: null,
    navSteps: null,
    defaultNavStep: null,
    defaultSubStep: null,
    actions: [],
    setTitle: () => {},
    setTitleRightSide: () => {},
    setTitleText: () => {},
    setNavSteps: () => {},
    setActions: () => {},
  }
});

export function useScreenContext() {
  return useContext(ScreenContext);
}

export function Screen({ children }: ScreenProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentNavStep = searchParams.get('navStep') || null;
  const currentSubStep = searchParams.get('subStep') || null;

  const [navState, setNavState] = useState<Pick<ScreenContextType['nav'], 'title' | 'titleText' | 'titleRightSide' | 'navSteps' | 'actions' | 'defaultNavStep' | 'defaultSubStep'>>({
    title: null,
    titleText: null,
    titleRightSide: null,
    navSteps: null,
    defaultNavStep: null,
    defaultSubStep: null,
    actions: [],
  });

  const setCurrentNavStep = useCallback((newStep: string) => {
    setSearchParams({ ...Object.fromEntries(searchParams), navStep: newStep }, { replace: true });
  }, [setNavState, searchParams]);

  const setCurrentSubStep = useCallback((newSubStep: string) => {
    setSearchParams({ ...Object.fromEntries(searchParams), subStep: newSubStep }, { replace: true });
  }, [setNavState, searchParams]);

  const  screenContext = useMemo<ScreenContextType>(() => ({
    nav: {
      ...navState,
      currentNavStep: currentNavStep || navState.defaultNavStep,
      currentSubStep: currentSubStep || navState.defaultSubStep,
      setTitle: (newTitle) => {
        setNavState(prev => ({ ...prev, title: newTitle }));
      },
      setTitleRightSide: (newTitleRightSide) => {
        setNavState(prev => ({ ...prev, titleRightSide: newTitleRightSide }));
      },
      setTitleText: (newTitleText) => {
        setNavState(prev => ({ ...prev, titleText: newTitleText }));
      },
      setNavSteps: ({ navSteps, defaultNavStep, defaultSubStep }) => {
        setNavState(prev => ({
          ...prev,
          navSteps: navSteps,
          defaultNavStep: defaultNavStep,
          defaultSubStep: defaultSubStep,
        }));
      },
      setActions: (actions) => {
        setNavState(prev => ({ ...prev, actions: actions }));
      },
    }
  }), [navState, currentNavStep, currentSubStep]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<LoadingSpinner />}>
        {(screenContext.nav.title || screenContext.nav.navSteps) && (
          <PageTitleAndNav
            title={screenContext.nav.title}
            titleText={screenContext.nav.titleText}
            titleRightSide={screenContext.nav.titleRightSide}
            currentStep={screenContext.nav.currentNavStep || screenContext.nav.defaultNavStep}
            currentSubStep={currentSubStep || screenContext.nav.defaultSubStep}
            handleStepChange={setCurrentNavStep}
            handleSubStepChange={setCurrentSubStep}
            steps={screenContext.nav.navSteps}
            actions={screenContext.nav.actions}
          />
        )}

        <ScreenContext.Provider value={screenContext}>
          {children}
        </ScreenContext.Provider>
      </Suspense>
    </ErrorBoundary>
  );
}

/**
 * HOC to wrap a component with Screen
 */
export function withScreen<P extends object>(
  WrappedComponent: ComponentType<P>,
) {
  return function ScreenWrapper(props: P) {
    return (
      <Screen>
        <WrappedComponent {...props} />
      </Screen>
    );
  };
} 